import {
  charts_cardTypePhysical,
  charts_cardTypeVirtual,
  charts_categorical1,
  charts_categorical2,
  charts_categorical3,
  charts_categorical4,
  charts_categorical5,
  charts_categorical6,
  charts_categoricalOther,
} from 'ext-ui/style/colors'
import {
  ChartCategoryPlan,
  ChartPlan,
  ChartSegmentPlan,
  ChartSeriesPlan,
  ChartTooltipDatum,
} from 'src/charting/plan/chartPlan.types'
import {
  ChartDataPrimitive,
  ChartDataRow,
  ChartSpec,
  ChartTooltipFieldSpec,
} from 'src/charting/spec/chartSpec.types'
import { assertValidChartSpec } from 'src/charting/spec/chartSpec.validation'

const colorTokens = [
  {
    name: 'charts_categorical1',
    color: charts_categorical1,
  },
  {
    name: 'charts_categorical2',
    color: charts_categorical2,
  },
  {
    name: 'charts_categorical3',
    color: charts_categorical3,
  },
  {
    name: 'charts_categorical4',
    color: charts_categorical4,
  },
  {
    name: 'charts_categorical5',
    color: charts_categorical5,
  },
  {
    name: 'charts_categorical6',
    color: charts_categorical6,
  },
]

const otherColorToken = {
  name: 'charts_categoricalOther',
  color: charts_categoricalOther,
}

const cardTypeColorTokens = {
  physical: {
    name: 'charts_cardTypePhysical',
    color: charts_cardTypePhysical,
  },
  virtual: {
    name: 'charts_cardTypeVirtual',
    color: charts_cardTypeVirtual,
  },
}

const singleSeriesKey = '__value__'

type ColorToken = {
  name: string
  color: string
}

const toKey = (value: ChartDataPrimitive | undefined): string => {
  if (value === undefined || value === null || value === '') return 'Unassigned'
  return String(value)
}

const toNumber = (value: ChartDataPrimitive | undefined): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    if (Number.isFinite(parsed)) return parsed
  }
  return 0
}

const titleForField = (field: ChartTooltipFieldSpec): string =>
  field.title ?? field.field

const normalizeTokenKey = (key: string): string =>
  key
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')

const getSemanticColorToken = (key: string): ColorToken | undefined => {
  const normalizedKey = normalizeTokenKey(key)

  if (normalizedKey === 'other') return otherColorToken
  if (
    normalizedKey === 'physical' ||
    normalizedKey === 'physicalcard' ||
    normalizedKey === 'physicalcards'
  ) {
    return cardTypeColorTokens.physical
  }
  if (
    normalizedKey === 'virtual' ||
    normalizedKey === 'virtualcard' ||
    normalizedKey === 'virtualcards'
  ) {
    return cardTypeColorTokens.virtual
  }

  return undefined
}

const getCategoricalColorToken = (
  usedColors: Set<string>,
  reservedColors: Set<string>,
): ColorToken =>
  colorTokens.find(
    (token) => !usedColors.has(token.color) && !reservedColors.has(token.color),
  ) ??
  colorTokens.find((token) => !usedColors.has(token.color)) ??
  otherColorToken

const getTooltip = (
  row: ChartDataRow,
  tooltipFields: ChartTooltipFieldSpec[] | undefined,
): ChartTooltipDatum[] =>
  (tooltipFields ?? []).map((field) => ({
    field: field.field,
    label: titleForField(field),
    value: row[field.field],
    valueFormat: field.valueFormat,
  }))

const getSeriesDefinitions = (
  rowsWithValue: ChartDataRow[],
  spec: ChartSpec,
): ChartSeriesPlan[] => {
  const colorField = spec.encoding.color
  const orderedKeys = new Set<string>()
  const visibleKeys = new Set<string>()

  if (!colorField) {
    if (rowsWithValue.length > 0) orderedKeys.add(singleSeriesKey)
  } else {
    rowsWithValue.forEach((row) => {
      visibleKeys.add(toKey(row[colorField.field]))
    })
    colorField.domain?.forEach((domainKey) => orderedKeys.add(toKey(domainKey)))
    rowsWithValue.forEach((row) =>
      orderedKeys.add(toKey(row[colorField.field])),
    )
  }

  const keys = Array.from(orderedKeys)
  const reservedSemanticColors = new Set(
    keys
      .map((key) => getSemanticColorToken(key)?.color)
      .filter((color): color is string => color !== undefined),
  )
  const usedColors = new Set<string>()

  return keys
    .map((key) => {
      const semanticToken = getSemanticColorToken(key)
      const token =
        semanticToken && !usedColors.has(semanticToken.color)
          ? semanticToken
          : getCategoricalColorToken(usedColors, reservedSemanticColors)

      usedColors.add(token.color)

      return {
        key,
        label:
          key === singleSeriesKey ? (spec.encoding.y.title ?? 'Value') : key,
        color: token.color,
        colorToken: token.name,
      }
    })
    .filter((series) => !colorField || visibleKeys.has(series.key))
}

const orderDivergingStackSegments = (category: ChartCategoryPlan): void => {
  const indexedSegments = category.segments.map((segment, index) => ({
    segment,
    index,
  }))
  const byLargestMagnitude = (
    a: (typeof indexedSegments)[number],
    b: (typeof indexedSegments)[number],
  ) =>
    Math.abs(b.segment.value) - Math.abs(a.segment.value) || a.index - b.index

  let totalPositive = 0
  let totalNegative = 0
  const positiveSegments = indexedSegments
    .filter(({ segment }) => segment.value >= 0)
    .sort(byLargestMagnitude)
    .map(({ segment }) => {
      const startValue = totalPositive
      totalPositive += segment.value
      return {
        ...segment,
        startValue,
        endValue: totalPositive,
      }
    })
  const negativeSegments = indexedSegments
    .filter(({ segment }) => segment.value < 0)
    .sort(byLargestMagnitude)
    .map(({ segment }) => {
      const startValue = totalNegative
      totalNegative += segment.value
      return {
        ...segment,
        startValue,
        endValue: totalNegative,
      }
    })

  category.segments = [...positiveSegments, ...negativeSegments]
  category.totalPositive = totalPositive
  category.totalNegative = totalNegative
}

export const compileStackedBarPlan = (
  spec: ChartSpec,
  rows: ChartDataRow[],
): ChartPlan => {
  assertValidChartSpec(spec)

  const xField = spec.encoding.x.field
  const yField = spec.encoding.y.field
  const colorField = spec.encoding.color?.field
  const rowsWithValue = rows.filter((row) => toNumber(row[yField]) !== 0)
  const series = getSeriesDefinitions(rowsWithValue, spec)
  const seriesByKey = new Map(series.map((item) => [item.key, item]))
  const categoryMap = new Map<string, ChartCategoryPlan>()
  const addCategory = (key: string) => {
    if (categoryMap.has(key)) return

    categoryMap.set(key, {
      key,
      label: key,
      index: categoryMap.size,
      totalPositive: 0,
      totalNegative: 0,
      segments: [],
    })
  }

  // Seed categories from an explicit x-domain first so the axis spans the full
  // range in order — values with no rows render as empty (zero) columns rather
  // than being dropped.
  spec.encoding.x.domain?.forEach((domainKey) => {
    addCategory(toKey(domainKey))
  })
  rows.forEach((row) => addCategory(toKey(row[xField])))

  rowsWithValue.forEach((row) => {
    const categoryKey = toKey(row[xField])
    const seriesKey = colorField ? toKey(row[colorField]) : singleSeriesKey
    const seriesPlan = seriesByKey.get(seriesKey) ?? series[0]
    const value = toNumber(row[yField])

    const category = categoryMap.get(categoryKey)
    if (!category || !seriesPlan) return

    const startValue =
      value < 0 ? category.totalNegative : category.totalPositive
    const endValue = startValue + value

    const segment: ChartSegmentPlan = {
      id: `${categoryKey}-${seriesKey}`,
      categoryKey,
      seriesKey,
      seriesLabel: seriesPlan.label,
      value,
      startValue,
      endValue,
      color: seriesPlan.color,
      colorToken: seriesPlan.colorToken,
      tooltip: getTooltip(row, spec.encoding.tooltip),
    }

    category.segments.push(segment)

    if (value < 0) {
      category.totalNegative = endValue
    } else {
      category.totalPositive = endValue
      category.totalNegative = Math.min(
        category.totalNegative,
        startValue,
        endValue,
      )
    }
  })

  const categories = Array.from(categoryMap.values())
  categories.forEach(orderDivergingStackSegments)

  const segmentValues = categories.flatMap((category) =>
    category.segments.flatMap((segment) => [
      segment.startValue,
      segment.endValue,
    ]),
  )
  const domainMin = Math.min(0, ...segmentValues)
  const domainMax = Math.max(0, ...segmentValues)

  return {
    kind: 'stacked-bar',
    title: spec.title,
    description: spec.description,
    ariaLabel: spec.ariaLabel ?? spec.title ?? 'Stacked bar chart',
    xAxisTitle: spec.encoding.x.title,
    yAxisTitle: spec.encoding.y.title,
    yValueFormat: spec.encoding.y.valueFormat ?? 'number',
    categories,
    series,
    yDomain: domainMin === domainMax ? [0, 1] : [domainMin, domainMax],
  }
}
